/**
 * Success page - payment confirmation after Stripe checkout
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { useCompany } from '@/hooks/useCompany';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { CheckCircleIcon, HomeIcon, HeartIcon } from '@heroicons/react/24/solid';
import type { TransactionResponse } from '@/types';

export function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verify } = useStripeCheckout();
  const { company } = useCompany();

  const [verifying, setVerifying] = useState(true);
  const [payment, setPayment] = useState<TransactionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(30);

  // Guard: ensure verify-payment is called exactly once per page load.
  // Without this, every re-render that changes `company` or `verify` would
  // re-fire the effect and create duplicate DB transactions / emails.
  const hasVerified = useRef(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setError('No session ID found. Payment verification failed.');
      setVerifying(false);
      return;
    }

    if (!company) {
      return; // Wait for company to load
    }

    // Prevent duplicate calls caused by company/verify reference changes
    if (hasVerified.current) {
      return;
    }
    hasVerified.current = true;

    const verifyPaymentAsync = async () => {
      try {
        setVerifying(true);
        const result = await verify(sessionId, company.companyId);
        setPayment(result);
        setError(null);
      } catch (err) {
        console.error('Payment verification error:', err);
        // Reset the guard so the user can retry on error
        hasVerified.current = false;
        setError(
          err instanceof Error ? err.message : 'Failed to verify payment. Please contact support.'
        );
      } finally {
        setVerifying(false);
      }
    };

    verifyPaymentAsync();
  }, [searchParams, verify, company]);

  // Countdown timer effect - starts after payment is verified
  useEffect(() => {
    if (!payment || error || verifying) {
      return;
    }

    if (countdown <= 0) {
      navigate('/');
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, payment, error, verifying, navigate]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-2/3 text-center space-y-6">
          <LoadingSpinner size="lg" />
          <h2 className="text-2xl font-semibold text-gray-800">Verifying your payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Try to determine if it was a service or donation from session, default to donation
    const isServicePayment = searchParams.get('purpose') === '2';

    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-150 w-full">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="bg-red-100 rounded-full p-3">
                <svg
                  className="h-12 w-12 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-red-900">Verification Failed</h2>
            <p className="text-red-700">{error}</p>
            <div className="pt-4 space-y-3">
              <button
                onClick={() => navigate(isServicePayment ? '/services' : '/donate')}
                className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
              >
                {isServicePayment ? 'Return to Services' : 'Return to Donations'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full py-2 px-4 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return null;
  }

  const transaction = payment.item;
  const isPaid = payment.status === 'success';
  const formattedAmount = transaction.totalAmount.toFixed(2);
  const isServicePayment = transaction.purposeId === 2;

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl w-full">
        {/* Success Header */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {/* Success Banner */}
          <div className="bg-linear-to-r from-green-500 to-emerald-600 px-6 py-8 sm:px-8 sm:py-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-4 shadow-lg">
                <CheckCircleIcon className="h-16 w-16 sm:h-20 sm:w-20 text-green-500" />
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
              {isServicePayment
                ? isPaid
                  ? 'Thank You for Your Payment!'
                  : 'Payment Received'
                : isPaid
                  ? 'Thank You for Your Donation!'
                  : 'Payment Received'}
            </h1>
            <p className="text-lg sm:text-xl text-green-50 mx-auto">
              {isServicePayment
                ? 'Your service payment has been successfully processed.'
                : 'Your generosity makes a difference in our community.'}
            </p>
          </div>

          {/* Payment Details */}
          <div className="px-6 py-8 sm:px-8 sm:py-10">
            <div className="bg-linear-to-br from-gray-50 to-gray-100 rounded-xl p-6 sm:p-8 space-y-6">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-6">
                {isServicePayment ? 'Service Payment Receipt' : 'Donation Receipt'}
              </h3>

              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 border-b-2 border-gray-300 gap-2">
                  <span className="text-lg font-medium text-gray-600">Amount</span>
                  <span className="text-3xl sm:text-4xl font-bold text-primary-600">
                    ${formattedAmount} {(transaction.currency || 'USD').toUpperCase()}
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 border-b border-gray-300 gap-2">
                  <span className="text-lg font-medium text-gray-600">Payment Status</span>
                  <span className="px-4 py-2 bg-green-500 text-white rounded-full text-base font-bold shadow-sm">
                    {isPaid ? 'Paid' : payment.status || 'Pending'}
                  </span>
                </div>

                {transaction.donorEmail && (
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 border-b border-gray-300 gap-2">
                    <span className="text-lg font-medium text-gray-600">Email</span>
                    <span className="text-base text-gray-900 font-medium break-all">
                      {transaction.donorEmail}
                    </span>
                  </div>
                )}

                {transaction.transactionId && (
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 border-b border-gray-300 gap-2">
                    <span className="text-lg font-medium text-gray-600">Transaction ID</span>
                    <span className="text-base text-gray-900 font-mono font-semibold">
                      #{transaction.transactionId}
                    </span>
                  </div>
                )}

                {transaction.providerToken && (
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start py-4 gap-2">
                    <span className="text-lg font-medium text-gray-600">Session ID</span>
                    <span className="text-xs text-gray-700 font-mono break-all">
                      {transaction.providerToken}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Email Notice */}
            {transaction.donorEmail && (
              <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                <p className="text-base sm:text-lg text-blue-900 text-center">
                  📧 A receipt has been sent to{' '}
                  <span className="font-bold">{transaction.donorEmail}</span>
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => navigate(isServicePayment ? '/services' : '/donate')}
                className="py-4 px-6 bg-linear-to-r from-primary-600 to-primary-700 text-white text-lg font-bold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 transform hover:scale-105"
              >
                <HeartIcon className="h-6 w-6" />
                {isServicePayment ? 'Continue Shopping' : 'Make Another Donation'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="py-4 px-6 border-2 border-gray-300 text-gray-700 text-lg font-bold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-3 transform hover:scale-105"
              >
                <HomeIcon className="h-6 w-6" />
                Return Home
              </button>
            </div>
          </div>
        </div>

        {/* Countdown Timer */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 rounded-full px-6 py-3 shadow-md">
            <svg className="h-5 w-5 text-gray-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-gray-700 font-medium">
              Redirecting to home in <span className="font-bold text-primary-600">{countdown}</span>{' '}
              seconds
            </span>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-4 text-center">
          <p className="text-gray-600 text-base">
            Questions about your {isServicePayment ? 'payment' : 'donation'}? Please contact us for
            assistance.
          </p>
        </div>
      </div>
    </div>
  );
}
